import type { ReactNode } from 'react'
import type { BookingDetailsNextPayment, BookingDetailsNextPaymentType } from '../../domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Divider } from '@sunwebgroup/uds-ui/divider'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { canAccessNewBookingPaymentPage } from '@/scopes/account/booking-payment/policies/canAccessNewBookingPaymentPage'
import { buildGroupPaymentUrl, buildPaymentUrl } from '@/scopes/account/booking-payment/policies/paymentUrl'
import { formatDate } from '@/scopes/account/common/formatters'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { useBookingDetailsStore } from '../../infrastructure/useBookingDetailsStore'
import { getNextPaymentDisplayDueDate, isClosedActiveChildGroupBooking, shouldHidePaymentDueDate } from '../selectors/paymentSelectors'
import { BookingDetailsSection } from './BookingDetailsSection'
import styles from './PaymentSection.module.css'

interface PaymentAmountRowProps {
  label: string
  value: string
  highlightValue?: boolean
  emphasized?: boolean
}

function PaymentAmountRow({
  label,
  value,
  highlightValue = false,
  emphasized = false,
}: PaymentAmountRowProps) {
  return (
    <div className={styles.row}>
      <Typography variant={emphasized ? 'body-l-bold-tight' : 'body-m-regular-tight'}>
        {label}
      </Typography>
      <div className={styles.rowRightAlign}>
        {highlightValue
          ? (
              <Tag variant="success-tinted">
                {value}
              </Tag>
            )
          : (
              <Typography
                variant={emphasized ? 'body-l-bold-tight' : 'body-m-bold-flat'}
              >
                {value}
              </Typography>
            )}
      </div>
    </div>
  )
}

const NEXT_PAYMENT_TYPE_LABEL: Record<BookingDetailsNextPaymentType, string> = {
  DownPayment: 'payment_down_payment',
  FullPayment: 'payment_amount_due',
  PartialPayment: 'payment_remaining_due',
}

function PaymentStatusPaid() {
  const intl = useIntl()
  const isCancelled = useBookingDetailsStore(state => state.isCancelled)

  const title = isCancelled
    ? intl.formatMessage({ id: 'payment_cancellation_confirmation_title' })
    : intl.formatMessage({ id: 'payment_confirmation_title' })

  return (
    <div className={styles.paidContainer}>
      <Icon className={styles.paidIcon} glyph="CheckMarkInCircle" size={40} />
      <Typography variant="body-m-bold-snug">{title}</Typography>
      {!isCancelled && (
        <Typography variant="body-s-regular-snug">
          {intl.formatMessage({ id: 'payment_confirmation_subtitle' })}
        </Typography>
      )}
    </div>
  )
}

interface PaymentStatusPaidTooMuchProps {
  refundAmount: number | null
}

function PaymentStatusPaidTooMuch({ refundAmount }: PaymentStatusPaidTooMuchProps) {
  const intl = useIntl()

  return (
    <>
      {refundAmount !== null && (
        <PaymentAmountRow
          label={intl.formatMessage({ id: 'payment_refund_sum' })}
          value={intl.formatCurrency(refundAmount)}
        />
      )}
      <Banner color="informative" size="M" hasIcon>
        <Banner.TextsWrapper>
          <Banner.SupportingText>
            {intl.formatMessage({ id: 'payment_refund_information' })}
          </Banner.SupportingText>
        </Banner.TextsWrapper>
      </Banner>
    </>
  )
}

interface PaymentStatusNotPaidProps {
  nextPayment: BookingDetailsNextPayment
}

function PaymentStatusNotPaid({ nextPayment }: PaymentStatusNotPaidProps) {
  const intl = useIntl()
  const locale = useConfiguration('locale')
  const siteName = useConfiguration('siteName')
  const bookingNumber = useBookingDetailsStore(state => state.bookingNumber)
  const isGroupBooking = useBookingDetailsStore(state => state.isGroupBooking)
  const isNewBookingPaymentPageEnabled = useFeatureFlag('ENABLE_MY_ACCOUNT_PAYMENT_PAGE')
  const isNewGroupsBookingPaymentPageEnabled = useFeatureFlag('KOS_ENABLE_MY_ACCOUNT_GROUPS_PAYMENT_PAGE')
  const shouldHideDueDate = useBookingDetailsStore(shouldHidePaymentDueDate)
  const displayDueDate = useBookingDetailsStore(getNextPaymentDisplayDueDate)
  const canUseNewBookingPaymentPage = canAccessNewBookingPaymentPage({
    isFlagEnabled: isNewBookingPaymentPageEnabled,
    isGroupPaymentFlagEnabled: isNewGroupsBookingPaymentPageEnabled,
    isGroupBooking,
  })
  const paymentUrl = canUseNewBookingPaymentPage
    ? (isGroupBooking
        ? buildGroupPaymentUrl(siteName, bookingNumber, nextPayment.paymentURL) ?? nextPayment.paymentURL
        : buildPaymentUrl(siteName, bookingNumber) ?? nextPayment.paymentURL)
    : nextPayment.paymentURL

  return (
    <>
      <div className={styles.rowWithSupportingText}>
        <PaymentAmountRow
          label={intl.formatMessage({ id: shouldHideDueDate ? 'payment_remaining_due_amount_urgent' : NEXT_PAYMENT_TYPE_LABEL[nextPayment.type] ?? 'payment_amount_due' })}
          value={intl.formatCurrency(nextPayment.amount)}
          emphasized
        />
        {displayDueDate && (
          <Typography className={styles.supportingText} variant="body-s-regular-snug">
            {intl.formatMessage({ id: 'payment_before_date' }, { date: formatDate(displayDueDate, locale) || displayDueDate })}
          </Typography>
        )}
      </div>
      <Button
        as="a"
        href={paymentUrl}
        className={styles.payButton}
        variant="primary"
        fullWidth
      >
        {intl.formatMessage({ id: 'payment_pay_now' })}
      </Button>
      <Typography className={styles.supportingText} variant="body-s-regular-snug">
        {intl.formatMessage({ id: 'payment_processing_information' })}
      </Typography>
    </>
  )
}

function PaymentStatusContent() {
  const payment = useBookingDetailsStore(state => state.payment)

  if (!payment)
    return null

  const content: ReactNode = (() => {
    switch (payment.status) {
      case 'Paid':
        return <PaymentStatusPaid />
      case 'PaidTooMuch':
        return <PaymentStatusPaidTooMuch refundAmount={payment.refundAmount} />
      case 'NotPaid':
        return payment.nextPayment
          ? <PaymentStatusNotPaid nextPayment={payment.nextPayment} />
          : null
      default:
        return null
    }
  })()

  return content
    ? (
        <>
          <Divider />
          {content}
        </>
      )
    : null
}

export function PaymentSection() {
  const intl = useIntl()
  const payment = useBookingDetailsStore(state => state.payment)
  const isCancelled = useBookingDetailsStore(state => state.isCancelled)
  const isClosedActiveChild = useBookingDetailsStore(isClosedActiveChildGroupBooking)

  if (isClosedActiveChild) {
    return (
      <Banner color="informative" size="M" hasIcon contentIsCentered>
        <Banner.TextsWrapper>
          <Banner.Text>
            {intl.formatMessage({ id: 'payment_closed_child_group_booking_description' })}
          </Banner.Text>
        </Banner.TextsWrapper>
      </Banner>
    )
  }

  if (!payment || !payment.isPaymentEnabled)
    return null

  const { totalAmount, amountPaid } = payment

  const totalAmountLabel = isCancelled
    ? intl.formatMessage({ id: 'payment_total_cancellation_sum' })
    : intl.formatMessage({ id: 'payment_total_sum' })

  return (
    <BookingDetailsSection isSidebar className={styles.container}>
      <PaymentAmountRow
        label={totalAmountLabel}
        value={intl.formatCurrency(totalAmount)}
      />
      <PaymentAmountRow
        label={intl.formatMessage({ id: 'payment_total_paid' })}
        value={intl.formatCurrency(amountPaid)}
        highlightValue={amountPaid > 0}
      />
      <PaymentStatusContent />
    </BookingDetailsSection>
  )
}
